import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime, Duration } from 'luxon';

import getOrdinalSuffix from '../../utils/ordinalSuffix';
import * as Styled from './styles';

const TIME_UNITS = [
    { key: 'days', label: 'days' },
    { key: 'hours', label: 'hours' },
    { key: 'minutes', label: 'mins' },
    { key: 'seconds', label: 'secs' }
];

const calculateTimeLeft = endDate => {
    const dateToDateTime = DateTime.fromMillis(Number(endDate), {
        zone: 'utc'
    });
    const now = DateTime.now();
    return dateToDateTime.diff(now, ['days', 'hours', 'minutes', 'seconds']);
};

const formatDuration = (millis, key) => {
    const formattedTimeUnit = Duration.fromMillis(millis)
        .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
        .toObject()[key];
    if (formattedTimeUnit < 0) {
        return '00';
    }
    if (key === 'days' && formattedTimeUnit > 365) {
        return '>365';
    }
    if (formattedTimeUnit < 10) {
        return formattedTimeUnit.toString().padStart(2, '0');
    }
    return formattedTimeUnit.toString();
};

const formatDateHeader = date => {
    const [weekday, day, month, hour, minute, meridiem] = DateTime.fromMillis(
        Number(date)
    )
        .toFormat('cccc d LLLL h mm a')
        .split(' ');

    const daySuffix = getOrdinalSuffix(parseInt(day, 10));

    const dateStr = `${weekday} ${day}${daySuffix} ${month}`;
    const timeStr = `${hour}:${minute}${meridiem.toLocaleLowerCase()}`;

    return `${dateStr} ${timeStr}`;
};

export const CountdownTimer = ({ date, name, showDateHeader, variant }) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(date));
    const dateHeaderStr = formatDateHeader(date);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeLeft(calculateTimeLeft(date));
        }, 1000);

        return () => clearInterval(interval);
    }, [date]);

    const units = TIME_UNITS.map(unit => ({
        ...unit,
        value: formatDuration(timeLeft, unit.key)
    }));
    // Tighten spacing only when the day count needs 3+ chars, to avoid overflow.
    const dense = variant === 'compact' && units[0].value.length > 2;

    return (
        <div>
            <Styled.CountdownWrapper variant={variant}>
                {showDateHeader && (
                    <Styled.DateTimeHeader>
                        {dateHeaderStr}
                    </Styled.DateTimeHeader>
                )}
                <Styled.Title variant={variant}>{name}</Styled.Title>
                <Styled.TimeWrapper variant={variant} dense={dense}>
                    {units.map(({ key, label, value }) => (
                        <Styled.TimeUnitWrapper key={key} variant={variant}>
                            <Styled.TimeUnit variant={variant}>
                                {value}
                            </Styled.TimeUnit>
                            <Styled.TimeUnitDescription variant={variant}>
                                {label}
                            </Styled.TimeUnitDescription>
                        </Styled.TimeUnitWrapper>
                    ))}
                </Styled.TimeWrapper>
            </Styled.CountdownWrapper>
        </div>
    );
};

CountdownTimer.defaultProps = {
    date: DateTime.now(),
    name: '',
    showDateHeader: true,
    variant: 'detail'
};

CountdownTimer.propTypes = {
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    showDateHeader: PropTypes.bool,
    variant: PropTypes.oneOf(['detail', 'compact'])
};

export default CountdownTimer;
